import React from "react";
import { useNavigate } from "react-router-dom";
import { Scan, User, LogOut, Home } from "lucide-react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { DevLinkProvider } from "../../devlink";
import { CategoryProvider } from "../Home/CategoryPublisherList";
import UserProvider, { useUserContext } from "../UserProvider";
import GoogleAuthProvider from "../GoogleAuthProvider";
import { ThemeProvider } from "../Home/navbar/ThemeToggle";
import { sendRequest } from "../../api";

const SettingsContent = () => {
  const navigate = useNavigate();
  const { userData } = useUserContext();
  const [scanAll, setScanAll] = React.useState(false);

  // Initialize scanAll state when userData changes
  React.useEffect(() => {
    if (userData) {
      setScanAll(userData.scan_all || false);
    }
  }, [userData]);

  const handleLogout = async () => {
    try {
      await sendRequest("/users/sign_out", "DELETE");
      navigate("/onboarding");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("Are you sure you want to delete your account? This action cannot be undone.")) {
      try {
        await sendRequest("/users", "DELETE");
        navigate("/onboarding");
      } catch (error) {
        console.error("Account deletion failed:", error);
      }
    }
  };

  const handleScanSettingChange = async (e) => {
    const newValue = e.target.checked;
    try {
      await sendRequest("/settings", "PUT", { settings: { scan_all: newValue } });
      setScanAll(newValue);
    } catch (error) {
      console.error("Failed to update scan settings:", error);
    }
  };

  if (!userData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">Settings</h1>
        <button
          onClick={() => navigate("/")}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 rounded-md"
        >
          <Home size={16} className="mr-2" />
          Go home
        </button>
      </div>
      
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">Account Information</h2>
          <p className="text-gray-600 mb-2">Email: {userData.email}</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">Scan Settings</h2>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={scanAll}
              onChange={handleScanSettingChange}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="text-gray-700">Scan Everything</span>
          </label>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">Account Actions</h2>
          <div className="space-y-4">
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md w-full"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </button>
            <button
              onClick={handleDeleteAccount}
              className="flex items-center px-4 py-2 text-red-600 hover:bg-gray-100 rounded-md w-full"
            >
              <User size={16} className="mr-2" />
              Close Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  return (
    <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID}>
      <DevLinkProvider>
        <CategoryProvider>
          <UserProvider>
            <GoogleAuthProvider>
              <ThemeProvider>
                <SettingsContent />
              </ThemeProvider>
            </GoogleAuthProvider>
          </UserProvider>
        </CategoryProvider>
      </DevLinkProvider>
    </GoogleOAuthProvider>
  );
};

export default Settings; 